import { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, FileText, Search, AlertTriangle, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { ContratoFormDialog } from "@/components/contratos/ContratoFormDialog";
import { ContratoCard } from "@/components/contratos/ContratoCard";
import { ArquivarDialog, ExcluirDialog, ReajusteDialog } from "@/components/contratos/ContratoActionDialogs";

const emptyForm = {
  tipo_pessoa: "PJ",
  razao_social: "",
  cnpj: "",
  telefone: "",
  responsavel: "",
  email: "",
  endereco: "",
  cep: "",
  cidade: "",
  estado: "",
  valor_aluguel: "",
  unidades_ids: [],
  data_inicio: "",
  data_fim: "",
  indice_reajuste: "IPCA",
  dia_vencimento: "10",
  multa_atraso_percent: "2",
  mora_diaria_percent: "0.033",
  taxa_agua_mensal: "0",
  taxa_rateio_fixa: "0",
  fiador: { nome: "", documento: "", telefone: "", email: "", endereco: "" },
  caucao: { valor: "", data_deposito: "", banco: "", observacoes: "" },
};

// Helpers puros — extraídos do body para não recriar toda render
const safeNum = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};
const safeInt = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};
const toISO = (d) => {
  if (!d) return undefined;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

export default function AdminContratos() {
  const [contratos, setContratos] = useState([]);
  const [unidadesLivres, setUnidadesLivres] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("criar");
  const [editingId, setEditingId] = useState(null);
  const [reajusteOpen, setReajusteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [arquivarOpen, setArquivarOpen] = useState(false);
  const [motivoArquivo, setMotivoArquivo] = useState("Fim de Contrato");
  const [motivoCustom, setMotivoCustom] = useState("");
  const [contratoSel, setContratoSel] = useState(null);
  const [percentualReajuste, setPercentualReajuste] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [busca, setBusca] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("todos");

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([api.get("/contratos"), api.get("/unidades")]);
    setContratos(c.data);
    setUnidadesLivres(u.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCriar = () => {
    setModalMode("criar");
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditar = (c) => {
    setModalMode("editar");
    setEditingId(c.id);
    setForm({
      tipo_pessoa: c.tipo_pessoa || "PJ",
      razao_social: c.razao_social || "",
      cnpj: c.cnpj || "",
      telefone: c.telefone || "",
      responsavel: c.responsavel || "",
      email: c.email || "",
      endereco: c.endereco || "",
      cep: c.cep || "",
      cidade: c.cidade || "",
      estado: c.estado || "",
      valor_aluguel: String(c.valor_aluguel || ""),
      unidades_ids: c.unidades_ids || [],
      data_inicio: (c.data_inicio || "").slice(0, 10),
      data_fim: (c.data_fim || "").slice(0, 10),
      indice_reajuste: c.indice_reajuste || "IPCA",
      dia_vencimento: String(c.dia_vencimento || 10),
      multa_atraso_percent: String(c.multa_atraso_percent ?? 2),
      mora_diaria_percent: String(c.mora_diaria_percent ?? 0.033),
      taxa_agua_mensal: String(c.taxa_agua_mensal ?? 0),
      taxa_rateio_fixa: String(c.taxa_rateio_fixa ?? 0),
      fiador: c.fiador || { nome: "", documento: "", telefone: "", email: "", endereco: "" },
      caucao: c.caucao ? {
        valor: String(c.caucao.valor || ""),
        data_deposito: (c.caucao.data_deposito || "").slice(0, 10),
        banco: c.caucao.banco || "",
        observacoes: c.caucao.observacoes || "",
      } : { valor: "", data_deposito: "", banco: "", observacoes: "" },
    });
    setModalOpen(true);
  };

  const salvar = async () => {
    // Validação prévia
    const faltando = [];
    if (!form.razao_social?.trim()) faltando.push(form.tipo_pessoa === "PF" ? "Nome completo" : "Razão social");
    if (!form.cnpj?.trim()) faltando.push(form.tipo_pessoa === "PF" ? "CPF" : "CNPJ");
    if (!form.telefone?.trim()) faltando.push("Telefone");
    if (!form.responsavel?.trim()) faltando.push("Responsável");
    const valorAluguelNum = safeNum(form.valor_aluguel, 0);
    if (valorAluguelNum <= 0) faltando.push("Aluguel base (> 0)");
    if (!form.data_inicio) faltando.push("Data de início");
    if (!form.data_fim) faltando.push("Data de fim");
    if (!Array.isArray(form.unidades_ids) || form.unidades_ids.length === 0) faltando.push("Ao menos 1 unidade");
    if (faltando.length) {
      toast.error(`Preencha: ${faltando.join(", ")}`);
      return;
    }

    const payload = {
      tipo_pessoa: form.tipo_pessoa,
      razao_social: form.razao_social.trim(),
      cnpj: form.cnpj.trim(),
      telefone: form.telefone.trim(),
      responsavel: form.responsavel.trim(),
      email: form.email?.trim() || undefined,
      endereco: form.endereco?.trim() || undefined,
      cep: form.cep?.replace(/\D/g, "") || undefined,
      cidade: form.cidade?.trim() || undefined,
      estado: form.estado?.trim().toUpperCase().slice(0, 2) || undefined,
      valor_aluguel: valorAluguelNum,
      unidades_ids: form.unidades_ids,
      data_inicio: toISO(form.data_inicio),
      data_fim: toISO(form.data_fim),
      indice_reajuste: form.indice_reajuste || "IPCA",
      dia_vencimento: safeInt(form.dia_vencimento, 10),
      multa_atraso_percent: safeNum(form.multa_atraso_percent, 2.0),
      mora_diaria_percent: safeNum(form.mora_diaria_percent, 0.033),
      taxa_agua_mensal: safeNum(form.taxa_agua_mensal, 0),
      taxa_rateio_fixa: safeNum(form.taxa_rateio_fixa, 0),
    };

    if (form.fiador?.nome?.trim()) {
      payload.fiador = {
        nome: form.fiador.nome.trim(),
        documento: form.fiador.documento || "",
        telefone: form.fiador.telefone || "",
        email: form.fiador.email || "",
        endereco: form.fiador.endereco || "",
      };
    }
    if (form.caucao?.valor) {
      const caucaoValorNum = safeNum(form.caucao.valor, 0);
      if (caucaoValorNum > 0) {
        payload.caucao = {
          valor: caucaoValorNum,
          data_deposito: toISO(form.caucao.data_deposito),
          banco: form.caucao.banco || "",
          observacoes: form.caucao.observacoes || "",
        };
      }
    }

    try {
      const resp = modalMode === "editar" && editingId
        ? await api.patch(`/contratos/${editingId}`, payload)
        : await api.post("/contratos", payload);
      const criado = resp?.data;
      toast.success(
        modalMode === "editar"
          ? "Contrato atualizado"
          : `Contrato criado${criado?.razao_social ? ` · ${criado.razao_social}` : ""}`
      );
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      setModalMode("criar");
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao criar contrato. Verifique os dados.");
    }
  };

  const confirmarExclusao = async () => {
    if (!contratoSel) return;
    try {
      const resp = await api.delete(`/contratos/${contratoSel.id}`);
      const data = resp?.data || {};
      toast.success(
        `Contrato de ${data.razao_social || contratoSel.razao_social} excluído · ${data.unidades_liberadas ?? 0} unidade(s) liberada(s) · ${data.boletos_pendentes_removidos ?? 0} boleto(s) pendente(s) removido(s)`
      );
      setDeleteOpen(false);
      setContratoSel(null);
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir");
    }
  };

  const abrirArquivar = (c) => {
    setContratoSel(c);
    setMotivoArquivo("Fim de Contrato");
    setMotivoCustom("");
    setArquivarOpen(true);
  };

  const confirmarArquivamento = async () => {
    if (!contratoSel) return;
    const motivo = motivoArquivo === "Outro" ? (motivoCustom.trim() || "Encerrado") : motivoArquivo;
    try {
      await api.post(`/contratos/${contratoSel.id}/arquivar`, { motivo });
      toast.success(`Contrato de ${contratoSel.razao_social} arquivado · unidades liberadas e histórico gravado`);
      setArquivarOpen(false);
      setContratoSel(null);
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao arquivar contrato");
    }
  };

  const restaurarContrato = async (c) => {
    try {
      const { data } = await api.post(`/contratos/${c.id}/restaurar`);
      const reoc = data?._reocupadas || [];
      const conf = data?._conflitos || [];
      if (conf.length) {
        toast.warning(`Contrato restaurado — ${reoc.length} unidade(s) reocupada(s), ${conf.length} unidade(s) em conflito: ${conf.join(", ")}`);
      } else {
        toast.success(`Contrato de ${c.razao_social} restaurado · ${reoc.length} unidade(s) reocupada(s)`);
      }
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao restaurar contrato");
    }
  };

  const aplicarReajuste = async () => {
    try {
      const pct = parseFloat(percentualReajuste);
      if (!Number.isFinite(pct) || pct <= 0) {
        toast.error("Informe um percentual válido");
        return;
      }
      await api.post(`/contratos/${contratoSel.id}/reajustar`, {
        percentual: pct,
        indice: contratoSel.indice_reajuste,
      });
      toast.success("Reajuste aplicado");
      setReajusteOpen(false);
      setPercentualReajuste("");
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao aplicar reajuste");
    }
  };

  // Regra "múltiplo": (a) contrato com ≥ 2 unidades OU (b) mesmo CNPJ tem ≥ 2 contratos ATIVOS
  const contagemPorDocumento = useMemo(() => {
    const map = {};
    contratos.forEach((c) => {
      if (c.status === "ARQUIVADO") return;
      const doc = (c.cnpj || "").trim();
      if (!doc) return;
      map[doc] = (map[doc] || 0) + 1;
    });
    return map;
  }, [contratos]);

  const isMultiplo = useCallback((c) => {
    if (c.status === "ARQUIVADO") return false;
    if ((c.unidades?.length ?? 0) >= 2) return true;
    return (contagemPorDocumento[(c.cnpj || "").trim()] || 0) >= 2;
  }, [contagemPorDocumento]);

  const isVencendoOuVencido = (c) => c.status !== "ARQUIVADO" && c.dias_para_termino != null && c.dias_para_termino <= 60;
  const isReajustePendente = (c) => c.status === "ATIVO" && (c.meses_desde_reajuste ?? 0) >= 12;

  const metricas = useMemo(() => ({
    ativos: contratos.filter((c) => c.status === "ATIVO").length,
    vencendo: contratos.filter(isVencendoOuVencido).length,
    multiplos: contratos.filter(isMultiplo).length,
    reajustePendente: contratos.filter(isReajustePendente).length,
    arquivados: contratos.filter((c) => c.status === "ARQUIVADO").length,
  }), [contratos, isMultiplo]);

  const contratosFiltrados = useMemo(() => {
    const buscaNorm = busca.trim().toLowerCase();
    return contratos.filter((c) => {
      if (abaAtiva === "ativos" && c.status !== "ATIVO") return false;
      if (abaAtiva === "vencendo" && !isVencendoOuVencido(c)) return false;
      if (abaAtiva === "multiplos" && !isMultiplo(c)) return false;
      if (abaAtiva === "arquivados" && c.status !== "ARQUIVADO") return false;
      if (abaAtiva === "todos" && c.status === "ARQUIVADO") return false;
      if (buscaNorm) {
        const alvo = [c.razao_social, c.responsavel, c.cnpj, ...(c.unidades || []).map((u) => u.numero_completo)]
          .filter(Boolean).join(" ").toLowerCase();
        if (!alvo.includes(buscaNorm)) return false;
      }
      return true;
    });
  }, [contratos, busca, abaAtiva, isMultiplo]);

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-tag mb-2">PJ ou PF · Garantia locatícia · N unidades por documento</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Contratos</h1>
        </div>
        <Button data-testid="btn-novo-contrato" onClick={openCriar}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
          <Plus size={14} className="mr-2" /> Novo contrato
        </Button>
      </div>

      <ContratoFormDialog
        open={modalOpen} onOpenChange={setModalOpen}
        mode={modalMode} form={form} setForm={setForm}
        unidadesLivres={unidadesLivres} onSave={salvar}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button type="button" data-testid="stat-ativos" onClick={() => setAbaAtiva("ativos")}
          className={`stat-block p-4 rounded-sm text-left transition ${abaAtiva === "ativos" ? "ring-2 ring-foreground" : "hover:border-foreground/40"}`}>
          <div className="label-tag flex items-center gap-1"><FileText size={10} /> Ativos</div>
          <div className="stat-num text-3xl mt-2 text-success">{metricas.ativos}</div>
        </button>
        <button type="button" data-testid="stat-vencendo" onClick={() => setAbaAtiva("vencendo")}
          className={`stat-block p-4 rounded-sm text-left transition ${abaAtiva === "vencendo" ? "ring-2 ring-foreground" : "hover:border-foreground/40"}`}>
          <div className="label-tag flex items-center gap-1"><AlertTriangle size={10} /> A vencer / vencidos (60d)</div>
          <div className={`stat-num text-3xl mt-2 ${metricas.vencendo > 0 ? "text-warning" : ""}`}>{metricas.vencendo}</div>
        </button>
        <button type="button" data-testid="stat-multiplos" onClick={() => setAbaAtiva("multiplos")}
          className={`stat-block p-4 rounded-sm text-left transition ${abaAtiva === "multiplos" ? "ring-2 ring-foreground" : "hover:border-foreground/40"}`}>
          <div className="label-tag flex items-center gap-1"><Users size={10} /> Lojistas c/ múltiplas</div>
          <div className="stat-num text-3xl mt-2">{metricas.multiplos}</div>
        </button>
        <button type="button" data-testid="stat-reajuste" onClick={() => setAbaAtiva("todos")}
          className="stat-block p-4 rounded-sm text-left transition hover:border-foreground/40">
          <div className="label-tag flex items-center gap-1"><RefreshCw size={10} /> Reajuste pendente (12m+)</div>
          <div className={`stat-num text-3xl mt-2 ${metricas.reajustePendente > 0 ? "text-info" : ""}`}>{metricas.reajustePendente}</div>
        </button>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="busca-contrato" value={busca} onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por lojista, responsável, CNPJ/CPF ou número da unidade..." className="pl-9 rounded-sm" />
        </div>
        <Tabs value={abaAtiva} onValueChange={setAbaAtiva}>
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="todos" data-testid="tab-todos">Todos ({contratos.filter((c) => c.status !== "ARQUIVADO").length})</TabsTrigger>
            <TabsTrigger value="ativos" data-testid="tab-ativos">Ativos ({metricas.ativos})</TabsTrigger>
            <TabsTrigger value="vencendo" data-testid="tab-vencendo">Vencendo/Vencidos ({metricas.vencendo})</TabsTrigger>
            <TabsTrigger value="multiplos" data-testid="tab-multiplos">Múltiplos ({metricas.multiplos})</TabsTrigger>
            <TabsTrigger value="arquivados" data-testid="tab-arquivados">Arquivados ({metricas.arquivados})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="text-xs text-muted-foreground">
          Exibindo <b className="text-foreground">{contratosFiltrados.length}</b> de <b className="text-foreground">{contratos.length}</b> contratos
        </div>
      </div>

      <div className="grid gap-4">
        {contratosFiltrados.map((c) => (
          <ContratoCard
            key={c.id}
            c={c}
            isMultiplo={isMultiplo(c)}
            isVencendoOuVencido={isVencendoOuVencido(c)}
            isReajustePendente={isReajustePendente(c)}
            onEdit={openEditar}
            onReajustar={(x) => { setContratoSel(x); setReajusteOpen(true); }}
            onArquivar={abrirArquivar}
            onRestaurar={restaurarContrato}
            onExcluir={(x) => { setContratoSel(x); setDeleteOpen(true); }}
          />
        ))}
      </div>

      {contratosFiltrados.length === 0 && (
        <div data-testid="empty-contratos" className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
          Nenhum contrato corresponde aos filtros.
        </div>
      )}

      <ArquivarDialog open={arquivarOpen} onOpenChange={setArquivarOpen}
        contrato={contratoSel} motivo={motivoArquivo} setMotivo={setMotivoArquivo}
        motivoCustom={motivoCustom} setMotivoCustom={setMotivoCustom}
        onConfirmar={confirmarArquivamento} />

      <ExcluirDialog open={deleteOpen} onOpenChange={setDeleteOpen}
        contrato={contratoSel} onConfirmar={confirmarExclusao} />

      <ReajusteDialog open={reajusteOpen} onOpenChange={setReajusteOpen}
        contrato={contratoSel} percentual={percentualReajuste} setPercentual={setPercentualReajuste}
        onAplicar={aplicarReajuste} />
    </div>
  );
}
